import React, { useRef, useEffect, useState } from 'react';
import { X, BookOpen, Trophy, Users, Award, Star, User } from 'lucide-react';
import { GameSession, Player } from '../../../lib/game/sessionService';
import { UserGame } from '../../../lib/game/collectionService';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';

// Interface para jogadores disponíveis/selecionados no modal
interface SessionPlayer {
  id: string;
  name: string;
  avatarUrl?: string;
  user_id?: string | null;
  isCurrentUser?: boolean;
}

interface GameSessionModalProps {
  isOpen: boolean;
  isEditing: boolean;
  isSaving: boolean;
  formData: Partial<GameSession>;
  selectedGame: UserGame | null;
  availablePlayers: SessionPlayer[];
  selectedPlayers: SessionPlayer[];
  winnerPlayerIds: Set<string>;
  modalRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onPlayerSelect: (playerId: string, selected: boolean) => void;
  onTogglePlayerWinner: (playerId: string) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
}

const GameSessionModal: React.FC<GameSessionModalProps> = ({
  isOpen,
  isEditing,
  isSaving,
  formData,
  selectedGame,
  availablePlayers,
  selectedPlayers,
  winnerPlayerIds,
  modalRef,
  onClose,
  onChange,
  onPlayerSelect,
  onTogglePlayerWinner,
  onSubmit,
}) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [currentUser, setCurrentUser] = useState<SessionPlayer | null>(null);
  const [showCurrentUserInWinners, setShowCurrentUserInWinners] = useState(false);
  const [showAllAvailablePlayers, setShowAllAvailablePlayers] = useState(false);

  // Efeito para definir o usuário atual quando o componente é montado
  useEffect(() => {
    if (user) {
      setCurrentUser({
        id: user.id,
        name: user.username || 'Você',
        avatarUrl: user.avatar_url,
        user_id: user.id,
        isCurrentUser: true
      });
    }
  }, [user]);

  // Efeito para verificar se devemos mostrar o usuário atual com base no resultado
  useEffect(() => {
    setShowCurrentUserInWinners(formData.result === 'vitória' || formData.result === 'empate' || formData.result === 'cooperativo');
  }, [formData.result]);

  const INITIAL_VISIBLE_PLAYERS = 3;
  const playersToDisplay = showAllAvailablePlayers 
    ? availablePlayers 
    : availablePlayers.slice(0, INITIAL_VISIBLE_PLAYERS);
  const hiddenPlayersCount = availablePlayers.length - INITIAL_VISIBLE_PLAYERS;

  if (!isOpen) return null;

  const getFallbackAvatar = (name: string) => {
    return name.substring(0, 2).toUpperCase();
  };

  // Lista de todos os jogadores para exibir na seção de vencedores
  const allPotentialWinners = [...selectedPlayers];
  
  // Adiciona o usuário atual à lista se for aplicável
  if (currentUser && showCurrentUserInWinners && !selectedPlayers.some(p => p.isCurrentUser)) {
    allPotentialWinners.unshift(currentUser);
  }

  // Classes condicionais para inputs e selects
  const inputBaseClasses = "w-full p-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500";
  const darkInputClasses = "dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:ring-indigo-500 dark:focus:border-indigo-500";
  const lightInputClasses = "border-gray-300";
  const finalInputClasses = `${inputBaseClasses} ${theme === 'dark' ? darkInputClasses : lightInputClasses}`;

  const labelClasses = `block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <div className={`fixed inset-0 ${theme === 'dark' ? 'bg-black bg-opacity-75' : 'bg-gray-500 bg-opacity-75'} flex items-center justify-center z-50 p-4`}>
      <div
        ref={modalRef}
        className={`${theme === 'dark' ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'} rounded-lg shadow-xl max-w-sm sm:max-w-lg md:max-w-xl lg:max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
      >
        <div className="p-4 sm:p-6 pb-20 sm:pb-24">
          <div className="flex justify-between items-center mb-4">
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
              {isEditing ? 'Editar Sessão de Jogo' : 'Registrar Nova Sessão'}
            </h3>
            <button
              onClick={onClose}
              className={`${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {selectedGame && (
            <div className={`mb-4 flex items-center p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
              {selectedGame.game?.thumb_url ? (
                <img
                  src={selectedGame.game.thumb_url.startsWith("http") ? selectedGame.game.thumb_url : (selectedGame.game.image_url || '/placeholder-game.png')}
                  alt={selectedGame.game?.name}
                  className="w-12 h-12 rounded object-cover mr-3"
                  onError={(e) => {
                    (e.target as HTMLImageElement).onerror = null;
                    (e.target as HTMLImageElement).src = selectedGame.game?.image_url || '/placeholder-game.png';
                  }}
                />
              ) : selectedGame.game?.image_url ? (
                <img
                  src={selectedGame.game.image_url}
                  alt={selectedGame.game?.name}
                  className="w-12 h-12 rounded object-cover mr-3"
                  onError={(e) => {
                    (e.target as HTMLImageElement).onerror = null;
                    (e.target as HTMLImageElement).src = '/placeholder-game.png';
                  }}
                />
              ) : (
                <div className={`w-12 h-12 rounded flex items-center justify-center mr-3 ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'}`}>
                  <BookOpen className={`h-6 w-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
                </div>
              )}
              <div>
                <h4 className={`font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{selectedGame.game?.name}</h4>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {selectedGame.game?.year ? `(${selectedGame.game.year})` : ''}
                </p>
              </div>
            </div>
          )}

          <form onSubmit={onSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Data e Hora */}
              <div className="col-span-1">
                <label className={labelClasses}>
                  Data e Hora da Partida
                </label>
                <input
                  type="datetime-local"
                  name="played_at"
                  value={formData.played_at || ''}
                  onChange={onChange}
                  className={finalInputClasses}
                  required
                  style={theme === 'dark' ? { colorScheme: 'dark' } : {}}
                />
              </div>

              {/* Duração */}
              <div className="col-span-1">
                <label className={labelClasses}>
                  Duração (minutos)
                </label>
                <input
                  type="number"
                  name="duration_minutes"
                  value={formData.duration_minutes || ''}
                  onChange={onChange}
                  className={finalInputClasses}
                  min="1"
                  placeholder="60"
                />
              </div>

              {/* Número de Jogadores */}
              <div className="col-span-1">
                <label className={labelClasses}>
                  Número de Jogadores
                </label>
                <input
                  type="number"
                  name="player_count"
                  value={formData.player_count || ''}
                  onChange={onChange}
                  className={finalInputClasses}
                  min="1"
                  placeholder="1"
                />
              </div>

              {/* Resultado */}
              <div className="col-span-1">
                <label className={labelClasses}>
                  Resultado
                </label>
                <select
                  name="result"
                  value={formData.result || ''}
                  onChange={onChange}
                  className={finalInputClasses}
                >
                  <option value="vitória" className={theme === 'dark' ? 'bg-gray-700 text-gray-100' : ''}>Vitória</option>
                  <option value="derrota" className={theme === 'dark' ? 'bg-gray-700 text-gray-100' : ''}>Derrota</option>
                  <option value="empate" className={theme === 'dark' ? 'bg-gray-700 text-gray-100' : ''}>Empate</option>
                  <option value="cooperativo" className={theme === 'dark' ? 'bg-gray-700 text-gray-100' : ''}>Cooperativo</option>
                  <option value="incompleto" className={theme === 'dark' ? 'bg-gray-700 text-gray-100' : ''}>Incompleto</option>
                </select>
              </div>

              {/* Local */}
              <div className="col-span-1">
                <label className={labelClasses}>
                  Local da Partida
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location || ''}
                  onChange={onChange}
                  className={finalInputClasses}
                  placeholder="Casa, Café, Luderia..."
                />
              </div>

              {/* Sua Pontuação */}
              <div className="col-span-1">
                <label className={labelClasses}>
                  Sua Pontuação
                </label>
                <input
                  type="number"
                  name="score"
                  value={formData.score === null || formData.score === undefined ? '' : formData.score}
                  onChange={onChange}
                  className={finalInputClasses}
                  min="0"
                  placeholder="100"
                />
              </div>

              {/* Pontuação Oponente */}
              <div className="col-span-1">
                <label className={labelClasses}>
                  Pontuação do Oponente
                </label>
                <input
                  type="number"
                  name="opponent_score"
                  value={formData.opponent_score === null || formData.opponent_score === undefined ? '' : formData.opponent_score}
                  onChange={onChange}
                  className={finalInputClasses}
                  min="0"
                  placeholder="80"
                />
              </div>
            </div>

            {/* Notas */}
            <div className="mt-4">
              <label className={labelClasses}>
                Notas Adicionais
              </label>
              <textarea
                name="notes"
                value={formData.notes || ''}
                onChange={onChange}
                className={`${finalInputClasses} min-h-[80px]`}
                placeholder="Detalhes da partida, momentos memoráveis..."
              />
            </div>

            {/* Seleção de Jogadores */}
            {availablePlayers && availablePlayers.length > 0 && (
              <div className="mt-6">
                <h4 className={`text-md font-medium mb-2 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>Jogadores Presentes</h4>
                <div className={`p-3 rounded-md border ${theme === 'dark' ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'} max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-2`}>
                  {playersToDisplay.map(player => (
                    <div key={player.id} className={`flex items-center p-2 rounded ${theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`}>
                      <input
                        type="checkbox"
                        id={`player-${player.id}`}
                        checked={selectedPlayers.some(p => p.id === player.id)}
                        onChange={(e) => onPlayerSelect(player.id, e.target.checked)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:bg-gray-600 dark:border-gray-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800"
                      />
                      <label htmlFor={`player-${player.id}`} className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                        {player.name}
                      </label>
                    </div>
                  ))}
                </div>
                {availablePlayers.length > INITIAL_VISIBLE_PLAYERS && (
                  <div className="mt-2 text-center">
                    <button
                      type="button"
                      onClick={() => setShowAllAvailablePlayers(!showAllAvailablePlayers)}
                      className={`text-sm font-medium ${theme === 'dark' ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'}`}
                    >
                      {showAllAvailablePlayers ? 'Ver Menos' : `Ver Mais (${hiddenPlayersCount})`}
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* Seleção de Vencedores */}
            {(formData.result === 'vitória' || formData.result === 'derrota' || formData.result === 'empate') && allPotentialWinners.length > 0 && (
                <div className="mt-6">
                    <h4 className={`text-md font-medium mb-2 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>Quem Venceu/Perdeu/Empatou?</h4>
                    <div className={`p-3 rounded-md border ${theme === 'dark' ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'} max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-2`}>
                        {allPotentialWinners.map(player => (
                            <div key={`winner-${player.id}`} className={`flex items-center justify-between p-2 rounded ${theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`}>
                                <div className="flex items-center">
                                    {player.avatarUrl ? (
                                        <img src={player.avatarUrl} alt={player.name} className="h-6 w-6 rounded-full mr-2" />
                                    ) : (
                                        <span className={`h-6 w-6 rounded-full mr-2 flex items-center justify-center text-xs font-semibold ${theme === 'dark' ? 'bg-gray-500 text-gray-100' : 'bg-gray-300 text-gray-700'}`}>
                                            {getFallbackAvatar(player.name)}
                                        </span>
                                    )}
                                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>{player.name} {player.isCurrentUser ? "(Você)" : ""}</span>
                                </div>
                                {formData.result !== 'empate' && (
                                <button
                                    type="button"
                                    onClick={() => onTogglePlayerWinner(player.id)}
                                    className={`px-2 py-0.5 text-xs rounded-full ${winnerPlayerIds.has(player.id) 
                                        ? (theme === 'dark' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-800') 
                                        : (theme === 'dark' ? 'bg-red-500 text-white' : 'bg-red-100 text-red-800')
                                    }`}
                                >
                                    {winnerPlayerIds.has(player.id) ? 'Vencedor' : 'Perdedor'}
                                </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Botões de Ação */}
            <div className="mt-6 pt-4 border-t flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 text-sm font-medium rounded-md border ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500`}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={`px-4 py-2 text-sm font-medium rounded-md text-white ${isSaving ? (theme === 'dark' ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-400 cursor-not-allowed') : (theme === 'dark' ? 'bg-indigo-500 hover:bg-indigo-600' : 'bg-indigo-600 hover:bg-indigo-700')} focus:outline-none focus:ring-2 focus:ring-offset-2 ${theme === 'dark' ? 'focus:ring-indigo-600 dark:ring-offset-gray-800' : 'focus:ring-indigo-500'}`}
              >
                {isSaving ? 'Salvando...' : (isEditing ? 'Salvar Alterações' : 'Registrar Partida')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GameSessionModal; 